// Brouillon d'e-mail (artefact de genre « email ») : fonctions pures de la carte.
// Mêmes règles que le serveur (backend/emails/draft.js) : adresses de forme WHATWG,
// « Nom <adresse> » accepté, séparées par des virgules ; heure d'envoi en heure d'Abidjan
// (UTC+0, sans heure d'été) ; vide = envoi immédiat. Aucune limite de volume (nombre de
// destinataires ou de pièces jointes, longueur de l'objet ou du message) : seulement les 10 Mo
// de pièces jointes du MCP.

import { normalizeDocumentSpec } from '../documents/documentSpec.js';

export const EMAIL_MAX_BYTES = 10 * 1024 * 1024;
export const ABIDJAN_TZ = 'Africa/Abidjan';

const ADDRESS_RE =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export const isAddress = (value) => typeof value === 'string' && value.length <= 254 && ADDRESS_RE.test(value);

/** Texte d'un champ (virgules, points-virgules ou retours à la ligne) → {valid, invalid}. */
export function parseAddresses(text) {
  const valid = [];
  const invalid = [];
  for (const part of String(text ?? '').split(/[,;\n]/)) {
    const raw = part.trim();
    if (!raw) continue;
    const angle = /<([^<>]+)>\s*$/.exec(raw);
    const address = (angle ? angle[1] : raw).trim();
    if (isAddress(address)) {
      if (!valid.some((a) => a.toLowerCase() === address.toLowerCase())) valid.push(address);
    } else invalid.push(raw);
  }
  return { valid, invalid };
}

export const domainOf = (address) => (isAddress(address) ? address.slice(address.lastIndexOf('@') + 1).toLowerCase() : null);

// Messageries publiques : jamais prises pour le domaine de l'entreprise. Libellé suivi d'un
// domaine de premier niveau (gmail.com, yahoo.fr, hotmail.co.uk), ou domaine exact.
const WEBMAIL_LABELS = new Set(['gmail', 'googlemail', 'yahoo', 'ymail', 'rocketmail', 'outlook', 'hotmail', 'live', 'msn', 'aol', 'gmx', 'protonmail', 'yandex']);
const WEBMAIL_DOMAINS = new Set(['icloud.com', 'me.com', 'mac.com', 'proton.me', 'pm.me', 'laposte.net', 'orange.fr', 'wanadoo.fr', 'free.fr', 'sfr.fr', 'neuf.fr', 'bbox.fr', 'mail.com', 'mail.ru', 'zoho.com', 'yopmail.com', 'tutanota.com']);

/** Domaine d'une messagerie publique (gmail.com, outlook.fr, orange.fr…) ? */
export function isPublicWebmail(domain) {
  const d = String(domain ?? '').toLowerCase();
  if (WEBMAIL_DOMAINS.has(d)) return true;
  const labels = d.split('.');
  if (labels.length === 2) return WEBMAIL_LABELS.has(labels[0]);
  // yahoo.co.uk, hotmail.com.br
  if (labels.length === 3 && /^(?:co|com|net|org)$/.test(labels[1]) && labels[2].length === 2) return WEBMAIL_LABELS.has(labels[0]);
  return false;
}

/**
 * Adresses à signaler « externes » : une adresse n'est interne que si elle a le domaine de
 * l'utilisateur ET que ce domaine n'est pas une messagerie publique (utilisateur en gmail.com :
 * toutes externes). Aucune sans adresse de l'utilisateur connue.
 */
export function externalAddresses(addresses, userEmail) {
  const own = domainOf(userEmail);
  if (!own) return [];
  const internal = (a) => domainOf(a) === own && !isPublicWebmail(own);
  return addresses.filter((a) => !internal(a));
}

/** Clé d'un fichier choisi (nom, taille, date de modification) : doublons ignorés. */
export const fileKey = (file) => `${file?.name}-${file?.size}-${file?.lastModified}`;

/**
 * Fichiers choisis → {accepted, refused: [texte]}, AVANT toute lecture : fichier vide écarté,
 * taille (file.size) comparée à ce qui reste des 10 Mo, pièces déjà jointes comprises (un
 * fichier trop lourd n'est jamais lu) ; doublons ignorés.
 */
export function screenFiles(list, { usedBytes = 0, knownKeys = [] } = {}) {
  const accepted = [];
  const refused = [];
  const seen = new Set(knownKeys);
  let used = usedBytes;
  for (const file of list || []) {
    const key = fileKey(file);
    if (seen.has(key)) continue;
    seen.add(key);
    const name = file?.name || 'Fichier';
    const size = Number(file?.size);
    if (!Number.isFinite(size) || size <= 0) {
      refused.push(`« ${name} » est vide : non joint.`);
      continue;
    }
    if (used + size > EMAIL_MAX_BYTES) {
      refused.push(`« ${name} » (${formatSize(size)}) non joint : ${formatSize(EMAIL_MAX_BYTES)} de pièces jointes au plus, il reste ${formatSize(Math.max(0, EMAIL_MAX_BYTES - used))}.`);
      continue;
    }
    used += size;
    accepted.push(file);
  }
  return { accepted, refused };
}

/** Pièce jointe générée expirée (fichiers conservés 24 h) ? */
export const isExpiredPiece = (piece, now = Date.now()) => Number.isFinite(piece?.expiresAt) && piece.expiresAt <= now;

/** Valeur d'un champ datetime-local (heure d'Abidjan = UTC) à partir d'une date ISO, ou ''. */
export function toAbidjanInput(iso) {
  const ms = Date.parse(iso ?? '');
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 16) : '';
}

/** Instant (ms) d'une valeur datetime-local lue en heure d'Abidjan, ou null. */
export function abidjanInputMs(value) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value ?? '')) return null;
  const ms = Date.parse(`${value}:00Z`);
  return Number.isFinite(ms) ? ms : null;
}

const dateFmt = new Intl.DateTimeFormat('fr-FR', { timeZone: ABIDJAN_TZ, day: 'numeric', month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('fr-FR', { timeZone: ABIDJAN_TZ, hour: '2-digit', minute: '2-digit' });

/** « 16 octobre 2026 à 08:30 », heure d'Abidjan ; '' pour une date illisible. */
export function formatAbidjan(iso) {
  const ms = Date.parse(iso ?? '');
  if (!Number.isFinite(ms)) return '';
  return `${dateFmt.format(ms)} à ${timeFmt.format(ms)}`;
}

export function formatSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}

/** Spécification reçue (serveur, ou rapport composé) → état initial de la carte. */
export function draftFromSpec(spec) {
  const s = spec && typeof spec === 'object' ? spec : {};
  const list = (v) => (Array.isArray(v) ? v.filter((a) => typeof a === 'string').join(', ') : typeof v === 'string' ? v : '');
  return {
    to: list(s.to),
    cc: list(s.cc),
    bcc: list(s.bcc),
    subject: typeof s.subject === 'string' ? s.subject : typeof s.title === 'string' ? s.title : '',
    body: typeof s.body === 'string' ? s.body : '',
    // Langue déclarée (prepare_email, rapport composé) : textes fixes du gabarit à l'envoi
    lang: s.lang === 'fr' || s.lang === 'en' ? s.lang : null,
    sendAt: toAbidjanInput(s.send_at),
    documents: (Array.isArray(s.attachments) ? s.attachments : [])
      .filter((a) => a && typeof a.download_url === 'string')
      .map((a) => ({
        id: String(a.id ?? a.download_url),
        title: a.title ?? a.filename ?? 'Document',
        filename: a.filename ?? null,
        format: a.format ?? null,
        size: Number.isFinite(a.size_bytes) ? a.size_bytes : null,
        downloadUrl: a.download_url,
        expiresAt: Number.isFinite(Date.parse(a.expires_at ?? '')) ? Date.parse(a.expires_at) : null,
      })),
  };
}

/**
 * Contrôle avant l'envoi → {errors: [texte], external: [adresse], payload} ;
 * payload : corps de POST /api/emails/send (sans le contexte).
 */
export function checkDraft(draft, { userEmail = null, files = [], now = Date.now() } = {}) {
  const errors = [];
  const fields = {};
  for (const [key, label] of [['to', 'À'], ['cc', 'Cc'], ['bcc', 'Cci']]) {
    const { valid, invalid } = parseAddresses(draft[key]);
    fields[key] = valid;
    if (invalid.length) errors.push(`${label} : adresse invalide « ${invalid[0]} »`);
  }
  if (!fields.to.length && !errors.some((e) => e.startsWith('À'))) errors.push('À : au moins un destinataire');
  if (!draft.subject?.trim()) errors.push('Objet obligatoire');
  if (!draft.body?.trim()) errors.push('Message vide');
  let sendAt = null;
  if (draft.sendAt) {
    const ms = abidjanInputMs(draft.sendAt);
    if (ms === null) errors.push("Date d'envoi illisible");
    else if (ms < now + 60_000) errors.push("Date d'envoi passée : videz-la pour un envoi immédiat");
    else sendAt = draft.sendAt;
  }
  const bytes = [...draft.documents, ...files].reduce((sum, p) => sum + (p.size || 0), 0);
  if (bytes > EMAIL_MAX_BYTES) errors.push(`Pièces jointes trop lourdes : ${formatSize(EMAIL_MAX_BYTES)} au plus`);
  // Document expiré (fichiers conservés 24 h) : signalé avant l'envoi, jamais au clic du serveur
  for (const doc of draft.documents.filter((d) => isExpiredPiece(d, now))) {
    errors.push(`Pièce jointe expirée : « ${doc.filename || doc.title} » ; retirez-la ou générez le document de nouveau`);
  }
  return {
    errors,
    external: externalAddresses([...fields.to, ...fields.cc, ...fields.bcc], userEmail),
    payload: {
      to: fields.to,
      cc: fields.cc,
      bcc: fields.bcc,
      subject: draft.subject.trim(),
      body: draft.body,
      ...(draft.lang ? { lang: draft.lang } : {}),
      ...(sendAt ? { send_at: sendAt } : {}),
      documents: draft.documents.map((d) => ({ download_url: d.downloadUrl })),
      fichiers: files.map((f) => ({ nom: f.nom, type_mime: f.type_mime, contenu_base64: f.contenu_base64 })),
    },
  };
}

// État d'une carte gardé côté serveur (backend/emails/envois.js), relu au montage sur tout
// poste : en_cours | envoye | programme | echec | annule | incertain
export const SEND_STATES = ['en_cours', 'envoye', 'programme', 'echec', 'annule', 'incertain'];
// Brouillon verrouillé : envoyé, programmé, en cours, à l'issue incertaine (jamais renvoyé sans
// vérification ni confirmation) ou annulé (« Modifier et renvoyer » le rouvre)
const LOCKED_STATES = ['en_cours', 'envoye', 'programme', 'incertain', 'annule'];
export const isLockedState = (statut) => LOCKED_STATES.includes(statut);

/**
 * Réponse 200 de l'envoi → état de la carte : un statut inconnu se lit « incertain » (jamais
 * un second envoi sans action explicite).
 */
export function sentState(email) {
  const e = email && typeof email === 'object' ? email : {};
  return { ...e, statut: SEND_STATES.includes(e.statut) ? e.statut : 'incertain' };
}

/**
 * Échec de POST /api/emails/send → 'uncertain' (l'e-mail est peut-être parti : vérifier avant
 * tout renvoi), 'in_progress' (envoi de ce brouillon déjà en cours) ou 'failed' (rien n'est
 * parti : corriger, puis envoyer). Connexion coupée ou réponse d'un intermédiaire (502, 504) :
 * la demande a pu arriver, l'issue est incertaine.
 */
export function sendFailureKind(error) {
  const code = error?.code ?? null;
  const status = Number(error?.status) || 0;
  if (code === 'EMAIL_SEND_UNCERTAIN') return 'uncertain';
  if (code === 'EMAIL_IN_PROGRESS' && status === 409) return 'in_progress';
  if (status === 0 || (status >= 500 && code !== 'EMAIL_SEND_FAILED')) return 'uncertain';
  return 'failed';
}

/** Libellé de l'état renvoyé par le serveur : {tone, text}. */
export function resultText(email) {
  if (!email) return null;
  if (email.statut === 'envoye') return { tone: 'success', text: email.envoye_le ? `Envoyé le ${formatAbidjan(email.envoye_le)}` : 'Envoyé' };
  if (email.statut === 'programme') return { tone: 'info', text: `Programmé pour le ${formatAbidjan(email.programme_pour) || 'moment choisi'} (heure d'Abidjan)` };
  if (email.statut === 'annule') return { tone: 'neutral', text: 'Envoi annulé' };
  if (email.statut === 'echec') return { tone: 'danger', text: `Échec de l'envoi${email.erreur ? ` : ${email.erreur}` : ''}` };
  if (email.statut === 'en_cours') return { tone: 'info', text: 'Envoi en cours…' };
  if (email.statut === 'incertain') return { tone: 'warn', text: 'Envoi incertain : l’e-mail est peut-être parti' };
  return { tone: 'info', text: email.message || 'Demande d’envoi transmise' };
}

// Brouillon du rapport composé, dans la langue du rapport (spec.lang, choisie par le serveur
// d'après la conversation : backend/documents/i18n.js) ; typographie de chaque langue
const REPORT_EMAIL = {
  fr: { fallback: 'Rapport', body: (title) => `Bonjour,\n\nVeuillez trouver ci-joint le rapport « ${title} ».\n\nCordialement,` },
  en: { fallback: 'Report', body: (title) => `Hello,\n\nPlease find attached the report “${title}”.\n\nBest regards,` },
};

/** Brouillon ouvert depuis un rapport composé : rapport en pièce jointe, destinataires à saisir. */
export function reportEmailSpec(doc) {
  const lang = doc?.lang === 'en' ? 'en' : 'fr';
  const title = doc?.title || doc?.filename || REPORT_EMAIL[lang].fallback;
  // Fin de validité du lien (expires_at, ou 24 h après la génération) : pièce signalée expirée
  const expiresAt = doc ? normalizeDocumentSpec(doc)?.expiresAt ?? null : null;
  return {
    kind: 'email',
    lang,
    to: [],
    cc: [],
    bcc: [],
    subject: title,
    body: REPORT_EMAIL[lang].body(title),
    attachments: [{ id: doc?.download_url, title, filename: doc?.filename ?? null, format: doc?.format ?? null, size_bytes: doc?.size_bytes ?? null, download_url: doc?.download_url, expires_at: expiresAt ? new Date(expiresAt).toISOString() : null }],
  };
}

/**
 * Document de l'aperçu (iframe sandbox, srcdoc) : le HTML exact de l'envoi (POST
 * /api/emails/preview), liens ouverts dans un nouvel onglet, hors du cadre, sans référent.
 */
export function previewDocument(html) {
  const head = '<base target="_blank"><meta name="referrer" content="no-referrer">';
  const text = String(html ?? '');
  return /<head[^>]*>/i.test(text) ? text.replace(/<head[^>]*>/i, (tag) => `${tag}${head}`) : `${head}${text}`;
}
